import { useMemo } from "react";
import { useAuth, useCachedTags, useTellescopeQuery, useUsersQuery } from "@tellescope/tanstack-query";
import { formatName, type FreeformFilterBlock } from "@tellescope/utils";
import type { ComboboxOption } from "@tellescope/ui";
import { getInboxChannelOptions, type InboxChannel, type InboxFilters } from "../../utils";

export interface UseInboxFilterConfigFormArgs {
  stagedFilters: InboxFilters;
  onStagedFiltersChange: (filters: InboxFilters) => void;
}

const generateBlockId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `block-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const makeEmptyBlock = (): FreeformFilterBlock => ({
  id: generateBlockId(),
  logic: "and",
  filters: [{ field: "", operation: "equals", value: undefined }],
});

/** Data loading and update handlers for the inbox filter form; the component only renders. */
export const useInboxFilterConfigForm = ({ stagedFilters, onStagedFiltersChange }: UseInboxFilterConfigFormArgs) => {
  const { selectedOrganization } = useAuth();
  const { data: availableTags } = useCachedTags({ model: "inbox_threads" });
  const { data: users = [] } = useUsersQuery();

  // A cleared field drops its key rather than holding `undefined`: dirty checks compare key
  // presence, so a lingering key reads as an unsaved change with no filter to show for it.
  const setFilterField = <K extends keyof InboxFilters>(key: K, value: InboxFilters[K]) => {
    if (value === undefined) {
      const { [key]: _cleared, ...rest } = stagedFilters;
      onStagedFiltersChange(rest);
      return;
    }
    onStagedFiltersChange({ ...stagedFilters, [key]: value });
  };

  const channelOptions = useMemo(() => {
    const showZendesk = !!selectedOrganization?.hasConnectedZendesk && !!selectedOrganization?.ticketThreadsEnabled;
    return getInboxChannelOptions().filter(({ value }) => value !== "Zendesk" || showZendesk);
  }, [selectedOrganization?.hasConnectedZendesk, selectedOrganization?.ticketThreadsEnabled]);

  const userOptions = useMemo<ComboboxOption[]>(
    () => users.map((user) => ({ value: user.id, label: formatName(user) || user.id })),
    [users]
  );

  // Selected assignees are resolved from their ids rather than remembered at click time, so a
  // reloaded or shared link still renders real names.
  const selectedAssigneeOptions = useMemo<ComboboxOption[]>(
    () =>
      (stagedFilters.assignees ?? []).map(
        (id) => userOptions.find((option) => option.value === id) ?? { value: id, label: id }
      ),
    [stagedFilters.assignees, userOptions]
  );

  const selectedCareTeamOptions = useMemo<ComboboxOption[]>(
    () =>
      (stagedFilters.careTeamIds ?? []).map(
        (id) => userOptions.find((option) => option.value === id) ?? { value: id, label: id }
      ),
    [stagedFilters.careTeamIds, userOptions]
  );

  const { data: selectedPatients } = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: { ids: stagedFilters.patients ?? [] },
    enabled: !!stagedFilters.patients?.length,
    select: (data) => data.matches,
  });

  const tagOptions = useMemo(() => {
    const selected = stagedFilters.tags ?? [];
    const merged = [...new Set([...availableTags, ...selected])];
    return merged.map((tag) => ({ value: tag, label: tag }));
  }, [availableTags, stagedFilters.tags]);

  const customBlocks = stagedFilters.customBlocks ?? [];

  return {
    channelOptions,
    userOptions,
    selectedAssigneeOptions,
    selectedCareTeamOptions,
    selectedPatients: selectedPatients ?? [],
    tagOptions,
    customBlocks,
    setFilterField,
    setChannels: (channels: string[]) =>
      setFilterField("channels", channels.length ? channels.filter(isInboxChannel) : undefined),
    addCustomBlock: () =>
      onStagedFiltersChange({ ...stagedFilters, customBlocks: [...customBlocks, makeEmptyBlock()] }),
    updateCustomBlock: (index: number, next: FreeformFilterBlock) =>
      onStagedFiltersChange({
        ...stagedFilters,
        customBlocks: customBlocks.map((block, i) => (i === index ? next : block)),
      }),
    removeCustomBlock: (index: number) =>
      onStagedFiltersChange({
        ...stagedFilters,
        customBlocks: customBlocks.filter((_, i) => i !== index),
      }),
  };
};

// Listed independently of the localized option getters, which must not be called at module scope.
const INBOX_CHANNELS: readonly InboxChannel[] = ["Email", "SMS", "Chat", "GroupMMS", "Phone", "Zendesk"];

const isInboxChannel = (value: string): value is InboxChannel => INBOX_CHANNELS.some((channel) => channel === value);
